import * as angular from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { ComponentDefinition, TagManualInputValues, IFrequency, IApplicationConfiguration, IValueViewModel, IDateRange, DigitalSet, RefreshClickData } from '../../../models';
import { IManualInputsService, ILoggerService, IDateAlignmentHelperService, ICurrentApplicationService, IAuthorizationService, IStoreService } from '../../../services';
import { refreshClick, manualInputDateFormats } from '../../../common';
import { DatesHelper } from '../../../common/datesHelper';
import { IManualInputsStateService } from '../../../states/services.index';
import { IManualInputsDataService } from '../../../widgets/manualInputs/manualInputsData.service';
import { Store, StoreElement } from '../../../stateManagement';

class ManualInputsStateController implements angular.IComponentController {
    application: IApplicationConfiguration;
    canEdit: boolean = false;
    currentBeginDate: string;
    currentEndDate: string;
    currentInterval: any;
    dateFormat = 'YYYY-MM-DD';
    dates: Date[];
    daysOff: number[];
    digitalsSets: DigitalSet[];
    filteredInputList: TagManualInputValues[];
    filteredVvms: IValueViewModel[];
    frequencies: IFrequency[];
    includeEndDate: boolean;
    indexDaysOff: number[];
    indexWeekBeginDay: number;
    initialDates: { beginDate: any, endDate: any };
    initialRange: IDateRange;
    inputList: TagManualInputValues[];
    inputValuesForm: angular.xeditable.IEditableFormController;
    isLoading: boolean;
    isMobileMode: boolean;
    loadingMessage: string;
    maxColumnsExceeded: boolean;
    selectedFrequency: IFrequency;
    validationEnabled: boolean;
    viewModels: TagManualInputValues[];
    vvms: IValueViewModel[];
    weekCalendarBeginDay: number;

    private _beginDate: Date;
    private _currentPage = 1;
    private _dateFormats = manualInputDateFormats;
    private _endDate: Date;
    private _maxColumns = 31;
    private _pristine: TagManualInputValues[];
    private _storeLoading: angular.IDeferred<void>;
    private _unsubscribe: Function;

    static $inject = [
        '$q',
        '$scope',
        'manualInputsService',
        'loggerService',
        'dateAlignmentHelperService',
        'currentApplicationService',
        'authorizationService',
        'manualInputsStateService',
        'manualInputsDataService',
        'storeService'
    ];

    constructor(
        private _$q: angular.IQService,
        private _$scope: angular.IScope,
        private _manualInputsService: IManualInputsService,
        private _loggerService: ILoggerService,
        private _dateAlignmentHelperService: IDateAlignmentHelperService,
        private _currentApplicationService: ICurrentApplicationService,
        private _authorizationService: IAuthorizationService,
        private _manualInputsStateService: IManualInputsStateService,
        private _manualInputsDataService: IManualInputsDataService,
        private _storeService: IStoreService
    ) {
    }

    $onInit() {
        this.application = this._currentApplicationService.getApplicationConfiguration();
        this.validationEnabled = this.application?.validationEnabled ?? false;
        this.weekCalendarBeginDay = this.application?.firstDayOfCalendarWeek ?? this.application?.weekBeginDay ?? -1;
        this.canEdit = this._authorizationService.canEdit();
        this.inputList = [];
        this.filteredVvms = [];

        this._$scope.$on('$destroy', () => this.$onDestroy());

        this.initStore().then(() => this.onFirstLoad());

        this._$scope.$watch(() => this.frequencies, (newValue, _oldValue) => {
            if (newValue && newValue.length) {
                const frequency = this.frequencies[0];
                const initialRange = this._manualInputsStateService.getInitialDates(this.initialDates);
                this.initialRange = this.alignDates(initialRange, frequency);
                this.selectFrequency(frequency);
            }
        });
    }

    $onDestroy() {
        this._unsubscribe();
    }

    private onFirstLoad(): void {
        this._$scope.$on(refreshClick, (_event, data: RefreshClickData) => {
            this._beginDate = new Date(data.beginDate);
            this._endDate = new Date(data.endDate);
            if (this.selectedFrequency) {
                // Beware data.beginDate/endDate are string but are used in loadData as Date
                this.loadData(data.beginDate, data.endDate, data.force);
            }
        });

        this._$scope.$watchGroup([() => this.filteredVvms, () => this.inputList], () => {
            if (this.filteredVvms && this.inputList) {
                this.applyFilters();
            }
        });
    }

    private initStore(): angular.IPromise<void> {
        this.isLoading = true;
        this._storeLoading = this._$q.defer<void>();

        this.frequencies = [];
        this.viewModels = [];

        this._unsubscribe = this._storeService.register(
            store => this.refreshStore(store),
            [
                StoreElement.ConnectionPoints,
                StoreElement.DigitalSets,
                StoreElement.Tags,
                StoreElement.ValueViewModels,
            ]
        );

        return this._storeLoading.promise;
    }

    private refreshStore(store: Store) {
        const defaultDatabaseGuid = this._currentApplicationService
            .getApplicationConfiguration()
            .defaultDatabaseGuid;

        const prepared = this._manualInputsStateService.prepareViewModels(store, defaultDatabaseGuid);

        this.digitalsSets = _.values(store.digitalSets.dictionary);
        this.viewModels = prepared.viewModels;
        this.frequencies = prepared.frequencies;
        this.isMobileMode = prepared.isMobileMode;
        this.isLoading = false;
        this._storeLoading.resolve();
    }

    private applyFilters() {
        const filtered = this.inputList.filter(i => _.some(this.filteredVvms, v => v.guid === i.guid));
        //#27824: Add virtual scroll and disable old paging
        this.filteredInputList = filtered;//.slice(0, this._currentPage * this._pageSize);
        //this.hasMore = filtered.length > this.inputList.length;
        //this.hasLess = this._currentPage > 1;
    }

    save() {
        this.isLoading = true;
        this.loadingMessage = 'Saving values';
        this._manualInputsService
            .save(this.filteredInputList)
            .then(count => {
                this.inputValuesForm.$cancel(); //WHY???
                this.inputValuesForm.$setPristine();
                this.confirmSuccess(count);
                return this.loadData(this._beginDate, this._endDate);
            })
            .finally(() => this.isLoading = false);
    }

    cancel() {
        this.inputValuesForm.$cancel();
        this.inputValuesForm.$setPristine();
        this.inputList = angular.copy(this._pristine);
    }

    private confirmSuccess(count: number): void {
        if (count > 0) {
            this._loggerService.logSuccess(`${count} new reprocessing tasks launched.`, true);
        }
        this._loggerService.logSuccess('Successfully saved manual inputs.', true);
        this.loadData(this._beginDate, this._endDate, true);
    }


    isValid(input: TagManualInputValues) {
        return input.values.filter(v => !!v.value).length > 0;
    }

    more() {
        this._currentPage++;
        this.applyFilters();
    }

    less() {
        if (this._currentPage > 1) {
            this._currentPage--;
            this.applyFilters();
        }
    }

    filterTag(list: IValueViewModel[]): IValueViewModel[] {
        const manuals = this.inputList.map(i => i.guid);
        list = list.filter(t => manuals.indexOf(t.guid) >= 0);
        return list;
    }

    selectFrequency(frequency: IFrequency) {
        this.selectedFrequency = frequency;
        this.dateFormat = this._dateFormats[frequency.intervalTimeUnit];
        this.inputList = this.viewModels.filter(vm => vm.frequency.interval === frequency.interval && vm.frequency.intervalTimeUnit === frequency.intervalTimeUnit);
        this.filteredInputList = this.inputList.map(i => i);
        this.vvms = this.inputList.map(i => i.viewModel);
        return this.loadData(this._beginDate, this._endDate);
    }

    private loadData(beginDate: any, endDate: any, force: boolean = false) {
        this.isLoading = true;
        this.loadingMessage = 'Reading values...';
        this.currentInterval = {
            beginDate: moment(beginDate),
            endDate: moment(endDate)
        };
        const frequency = this.selectedFrequency;
        return this._manualInputsDataService.getRange(beginDate, endDate, frequency, this._maxColumns)
            .then(res => {
                const { dates, isExceeded } = res;
                this.dates = dates;
                this.maxColumnsExceeded = isExceeded;
                this.inputList = this.inputList.map(d => {
                    const retVal = angular.copy(d);
                    retVal.values = this.dates.map(date => {
                        return {
                            date: date,
                            value: null
                        };
                    });
                    return retVal;
                });
                return this._manualInputsDataService.loadData(dates, this.inputList, this.validationEnabled, force, (outIndex, inIndex) => {
                    const fieldName = `value_${outIndex}_${inIndex}`;
                    if (this.inputValuesForm && this.inputValuesForm[fieldName]) {
                        this.inputValuesForm[fieldName].$setDirty();
                    }
                });
            })
            .then(inputList => {
                this.inputList = inputList;
                this._pristine = angular.copy(this.inputList);
            })
            .catch(reason => {
                this._loggerService.logError(reason);
            })
            .finally(() => this.isLoading = false);
    }

    private alignDates(initialRange: IDateRange, frequency: IFrequency): IDateRange {
        if (initialRange && this.application) {
            let alignedEndDate = this._dateAlignmentHelperService.previous(
                initialRange.endDate.clone(),
                frequency,
                this.application.beginDayTime,
                this.application.weekBeginDay,
                this.application.timeZone
            );
            let alignedBeginDate = this._dateAlignmentHelperService.previous(
                initialRange.endDate.clone(),
                frequency,
                this.application.beginDayTime,
                this.application.weekBeginDay,
                this.application.timeZone
            );
            const period = DatesHelper.timeUnitToStartOf(frequency.intervalTimeUnit);
            alignedEndDate = alignedEndDate.add(frequency.interval, period);
            alignedBeginDate = alignedBeginDate.add(frequency.interval, period);
            alignedBeginDate = alignedBeginDate.add(-7 * frequency.interval, period);
            // prevent infinite loop on F5
            alignedBeginDate.milliseconds(0);
            alignedEndDate.milliseconds(0);
            this._beginDate = alignedBeginDate.toDate();
            this._endDate = alignedEndDate.toDate();
            initialRange = {
                beginDate: alignedBeginDate,
                endDate: alignedEndDate
            };
        }

        return initialRange;
    }

    realignDates() {
        if (this.inputList && this.inputList.length > 0) {
            const newBeginDate = this.inputList[0].values[0].date;
            this.initialRange = {
                beginDate: moment(newBeginDate),
                endDate: moment(this._endDate)
            };
            this._beginDate = newBeginDate;
        }
    }
}

export const wtManualInputsState: ComponentDefinition = {
    componentId: 'wtManualInputsState',
    controller: ManualInputsStateController,
    template: require('./manualInputsState.component.html'),
    controllerAs: 'vm',
    bindings: {
        initialDates: '<',
        isMobileMode: '<?'
    }
};